import { createStitches } from '@stitches/react';
import type * as Stitches from '@stitches/react';
import { palette } from './theme/palette';
import { scale } from './theme/scale';

export const { styled, css, globalCss, keyframes, getCssText, theme, createTheme, config } =
  createStitches({
    theme: {
      colors: {
        textTertiaryAlt: palette.gold600,
        textTertiary: palette.gold300,
        textSecondaryAlt: palette.grey400,
        textSecondary: palette.neutral300,
        textPrimaryAlt: palette.grey900,
        textPrimary: palette.white,
        textBlack: palette.neutral900,
        textDisabled: palette.neutral600,

        webappPrimary: palette.neutral100,
        webappSecondary: palette.neutral300,
        webappTertiary: palette.neutral500,

        colorGoldOnDark: palette.gold600,
        colorGoldOnLight: palette.gold300,
        colorSkyLight: palette.grey100,
        colorSkyDark: palette.grey200,
        colorGreyLv5: palette.grey400,
        colorGreyLv4: palette.grey500,
        colorGreyLv3: palette.grey700,
        colorGreyLv2: palette.grey800,
        colorGreyLv1: palette.grey900,
        colorGreyLv1Trans: palette.grey900Trans,
        colorBackground: palette.black,
        colorGradientDarkX: `linear-gradient(90deg, ${palette.sky400} 0%, ${palette.gold300} 100%)`,
        colorGradientDarkY: `linear-gradient(180deg, ${palette.gold300} 0%, ${palette.sky400} 100%)`,
        colorGradientBlack: `linear-gradient(180deg, ${palette.black} 0%, ${palette.grey900} 100%)`,
        colorGradientBlackBorder: `linear-gradient(0deg, ${palette.grey800}00 0%, ${palette.grey800} 100%)`,
        colorGradientWhiteBorder: `linear-gradient(180deg, ${palette.grey800}33 0%, ${palette.grey800}00 100%)`,
        colorGradientPrimary: `linear-gradient(116deg, #5682F2 -36.05%, ${palette.brand400} 100.05%)`,
        colorProgressOnDark: palette.sky900,
        colorProgressOnLight: palette.sky100,
        colorPrimaryText: palette.white,

        // surface
        surfacePrimary: palette.neutral900,
        surfaceSecondary: palette.neutral800,
        surfaceTertiary: palette.neutral700,
        surfaceDisabled: 'rgba(110, 114, 122, 0.2)',

        // border
        borderPrimary: palette.neutral600,
        borderSecondary: palette.neutral700,
        borderSelected: palette.brand500,
        borderOpacity: 'rgba(237, 240, 245, 0.15)',
      },
      fonts: {
        telegraf: 'Telegraf',
        montreal: 'Neue Montreal',
      },
      radii: {
        1: '4px',
        2: '8px',
        3: '12px',
        4: '16px',
        5: '20px',
        none: '0',
        round: '50%',
        pill: '9999px',
      },
      space: scale,
      sizes: scale,
      fontWeights: {
        regular: 400,
        medium: 500,
        semiBold: 600,
        bold: 700,
      },
      fontSizes: {
        1: '12px',
        2: '13px',
        3: '15px',
        4: '17px',
        5: '19px',
        6: '21px',
        7: '27px',
        8: '35px',
        9: '48px',
        10: '59px',
      },
      zIndices: {
        behind: '-1',
        1: 1,
        2: 10,
        3: 100,
        4: 1000,
      },
    },
    media: {
      xs: '(max-width: 640px)',
      sm: '(max-width: 768px)',
      md: '(max-width: 1024px)',
      lg: '(max-width: 1280px)',
      xl: '(max-width: 1536px)',
      xxl: '(min-width: 1536px)',
    },
    utils: {
      m: (value: Stitches.PropertyValue<'margin'>) => ({
        margin: value,
      }),
      mt: (value: Stitches.PropertyValue<'marginTop'>) => ({
        marginTop: value,
      }),
      mr: (value: Stitches.PropertyValue<'marginRight'>) => ({
        marginRight: value,
      }),
      mb: (value: Stitches.PropertyValue<'marginBottom'>) => ({
        marginBottom: value,
      }),
      ml: (value: Stitches.PropertyValue<'marginLeft'>) => ({
        marginLeft: value,
      }),
      mx: (value: Stitches.PropertyValue<'marginLeft'>) => ({
        marginLeft: value,
        marginRight: value,
      }),
      my: (value: Stitches.PropertyValue<'marginTop'>) => ({
        marginTop: value,
        marginBottom: value,
      }),
      truncateText: (width: number) => ({
        maxWidth: width,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      pt: (value: Stitches.PropertyValue<'paddingTop'>) => ({
        paddingTop: value,
      }),
      pb: (value: Stitches.PropertyValue<'paddingBottom'>) => ({
        paddingBottom: value,
      }),
      pr: (value: Stitches.PropertyValue<'paddingRight'>) => ({
        paddingRight: value,
      }),
      pl: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
        paddingLeft: value,
      }),
      px: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
        paddingLeft: value,
        paddingRight: value,
      }),
      py: (value: Stitches.PropertyValue<'paddingTop'>) => ({
        paddingTop: value,
        paddingBottom: value,
      }),
    },
  });
